<template>
  <div style="padding: 20px">
    <v-dialog v-model="etsy_data_dialog" scrollable max-width="800px">
      <v-card style="padding: 20px; height: 100%">
        <div v-if="fetching_etsy_data" style="padding: 60px 0px" class="text-center">
          <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
        </div>
        <div v-else>
          <div class="mt-5 mb-5 text-center" v-if="!etsy_data.success">
            <h2>Something went wrong!!</h2>
          </div>
          <div v-else>
            <json-viewer :value="etsy_data.response" copyable expanded></json-viewer>
          </div>
        </div>
      </v-card>
    </v-dialog>
    
    <!-- Image Difference Modal -->
    <v-dialog v-model="imageDifferenceDialog" scrollable max-width="900px">
      <v-card>
        <v-card-title class="headline">
          Realtime Image Comparison
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDifferenceDialog = false">
              <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="loadingImages" style="padding: 60px 0px" class="text-center">
            <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
            <p class="mt-2">Loading realtime image comparison data...</p>
          </div>
          <div v-else-if="!imageData.success" class="text-center py-4">
            <v-icon large color="error">{{ icons.mdiAlertCircle }}</v-icon>
            <p class="mt-2">{{ imageData.message || 'Failed to load realtime image comparison' }}</p>
          </div>
          <div v-else>
            <v-row class="mb-4">
              <v-col cols="6" class="text-center">
                <h3>Etsy Images ({{ imageData.data.etsy_image_count }})</h3>
              </v-col>
              <v-col cols="6" class="text-center">
                <h3>Shopify Images ({{ imageData.data.shopify_image_count }})</h3>
              </v-col>
            </v-row>
            
            <div v-for="(_, index) in Math.max(imageData.data.etsy_images.length, imageData.data.shopify_images.length)" :key="index" class="mb-6">
              <v-row>
                <v-col cols="6" class="text-center">
                  <v-card outlined v-if="index < imageData.data.etsy_images.length" class="mx-auto" max-width="400">
                    <v-img
                      :src="imageData.data.etsy_images[index].url"
                      max-height="180"
                      contain
                      class="grey lighten-2"
                    ></v-img>
                    <v-card-text class="text-center">
                      Image #{{ index + 1 }}
                    </v-card-text>
                  </v-card>
                  <v-card v-else outlined class="mx-auto pa-4 grey lighten-3" max-width="400" height="300">
                    <div class="text-center my-12">
                      <v-icon large>mdi-image-off</v-icon>
                      <p>No image</p>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="6" class="text-center">
                  <v-card outlined v-if="index < imageData.data.shopify_images.length" class="mx-auto" max-width="400">
                    <v-img
                      :src="imageData.data.shopify_images[index].url"
                      max-height="180"
                      contain
                      class="grey lighten-2"
                    ></v-img>
                    <v-card-text class="text-center">
                      Image #{{ index + 1 }}
                    </v-card-text>
                  </v-card>
                  <v-card v-else outlined class="mx-auto pa-4 grey lighten-3" max-width="400" height="300">
                    <div class="text-center my-12">
                      <v-icon large>mdi-image-off</v-icon>
                      <p>No image</p>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-data-table :headers="headers" :items="products.data || []" class="elevation-1" hide-default-footer disable-pagination>
      <template v-slot:top>
        <v-row style="padding: 10px">
          <v-col md="2" cols="6"> <strong>Total: </strong> {{ products.total || 0 }} </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="searchTerm"
              label="Search Keyword"
              outlined
              dense
              placeholder="Search Keyword"
              @change="fetchEtsyProducts()"
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </template>
      <template v-slot:body="{ items }">
        <tbody v-if="!is_loading">
          <tr v-if="!items || !items.length">
            <td colspan="3" class="text-center" style="padding: 20px 0px">
              <img :src="require('@/assets/images/misc/no-data.png')" alt="" width="250px" />
              <p style="font-size: 25px">No Data Found!!</p>
            </td>
          </tr>
          <tr v-else v-for="item in items" :key="item.id">
            <td style="padding: 10px; width: 45%" :set="(etsy_images = item.fetched_imagesdata ? JSON.parse(item.fetched_imagesdata) : null)">
              <div style="display: flex">
                <img
                  :src="
                    etsy_images && etsy_images[0] && etsy_images[0].url_fullxfull
                      ? etsy_images[0].url_fullxfull
                      : require('@/assets/images/misc/no-image.png')
                  "
                  width="100px"
                  height="100px"
                  alt=""
                  class="mr-3"
                />
                <div style="flex: 1; min-width: 0;">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span 
                        class="primary--text text-truncate d-inline-block" 
                        style="max-width: 200px; cursor: pointer;"
                        @click="etsyProductDetail(item.id)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ (item.title || '') | truncate(50, true) }}
                      </span>
                    </template>
                    <span>{{ item.title }}</span>
                  </v-tooltip>
                  <p class="mt-2"><strong>Listing ID:</strong> {{ item.listing_id }}</p>
                  <p class="mt-2"><strong>SKU: <span v-html="showSKUStatus(item)"></span></strong></p>
                </div>
              </div>
            </td>
            <td
              v-if="item.shopify_product"
              style="padding: 10px; width: 45%"
              :set="(shopify_attrs = item.shopify_product.attributes ? JSON.parse(item.shopify_product.attributes) : null)"
            >
              <div style="display: flex">
                <img
                  :src="
                    shopify_attrs && shopify_attrs.images && shopify_attrs.images.length > 0 && shopify_attrs.images[0] && shopify_attrs.images[0].src ? shopify_attrs.images[0].src : require('@/assets/images/misc/no-image.png')
                  "
                  width="100px"
                  height="100px"
                  alt=""
                  class="mr-3"
                />
                <div style="flex: 1; min-width: 0;">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <span 
                        class="primary--text text-truncate d-inline-block" 
                        style="max-width: 200px; cursor: pointer;"
                        @click="shopifyProductDetail(item.shopify_product.id)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ (item.shopify_product.title || '') | truncate(50, true) }}
                      </span>
                    </template>
                    <span>{{ item.shopify_product.title }}</span>
                  </v-tooltip>
                  <p class="mt-2"><strong>Shopify ID:</strong> {{ item.shopify_product.shopify_id }}</p>
                </div>
              </div>
            </td>
            <td v-else>Something Went Wrong!!</td>
            <td class="">
              <div style="display: flex; align-items: center; gap: 3px;">
              <v-btn color="success" @click="setProductOnSync(item)" class="text-white mb-1 btn-xs" small
                >
                <v-icon left>{{ icons.mdiSync }}</v-icon>
                Sync Now</v-btn
              >
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="info" class="mb-1 ml-1 btn-xs" v-bind="attrs" v-on="on" small>
                    <v-icon small>{{ icons.mdiMenu }}</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="showImageDifference(item)">
                    <v-list-item-title>
                      <v-icon left>{{ icons.mdiImageMultipleOutline }}</v-icon>
                      Realtime Image Comparison</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="getEtsyApiDetail(item.listing_id)">
                    <v-list-item-title>
                      <v-icon left>{{ icons.mdiCodeBracesBox }}</v-icon>
                      View Raw</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="forceSyncProduct(item)">
                    <v-list-item-title>
                      <v-icon left>{{ icons.mdiSyncAlert }}</v-icon>
                      Force Sync
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6" class="text-center" style="padding: 80px 0px">
              <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <v-pagination
      v-model="products.current_page"
      class="my-4"
      @input="handlePageChange"
      :length="products.last_page"
      :total-visible="7"
    ></v-pagination>
  </div>
</template>
<script>
import store from '@/store'
import JsonViewer from 'vue-json-viewer'
import router from '@/router'
import { mdiClose, mdiAlertCircle, mdiMenu, mdiSync, mdiCodeBracesBox, mdiImageMultipleOutline, mdiSyncAlert } from '@mdi/js'

export default {
  props: {
    search: String,
  },
  data() {
    return {
      shop: null,
      is_loading: false,
      configurations: {},
      products: {
        current_page: 1,
        data: [],
      },
      headers: [{ text: 'Etsy Product', value: 'title' }, { text: 'Shopify Product' }, { text: 'Actions' }],
      icons: {
        mdiClose: mdiClose,
        mdiAlertCircle: mdiAlertCircle,
        mdiMenu: mdiMenu,
        mdiSync: mdiSync,
        mdiCodeBracesBox: mdiCodeBracesBox,
        mdiImageMultipleOutline: mdiImageMultipleOutline,
        mdiSyncAlert: mdiSyncAlert,
      },
      etsy_data_dialog: false,
      fetching_etsy_data: false,
      etsy_data: {},
      product_count: {},
      searchTerm: this.search,
      imageDifferenceDialog: false,
      loadingImages: false,
      imageData: {
        success: false,
        data: {
          etsy_images: [],
          shopify_images: [],
          etsy_image_count: 0,
          shopify_image_count: 0
        }
      },
    }
  },
  components: {
    JsonViewer,
  },
  watch: {
    search(newVal) {
      this.searchTerm = newVal;
    }
  },
  mounted() {
    this.shop = store.getters.loggedInShop
    this.fetchEtsyProducts()
  },
  methods: {
    async fetchEtsyProducts() {
      try {
        this.is_loading = true
        this.$emit('updateSearch', this.searchTerm);
        let response = await axios.get(`/admin/shops/${this.shop.id}/get-etsy-products`, {
          params: {
            shop_id: this.shop.id,
            type: 1,
            page: this.products.current_page,
            search: this.searchTerm,
          },
        })
        if(response && response.data)
        {
            this.products = {
              current_page: response.data.current_page || 1,
              data: response.data.data || [],
              total: response.data.total || 0,
              last_page: response.data.last_page || 1
            }
            this.product_count = response.data.product_count || {}
            this.$emit('count-updated', this.product_count);
        } else {
          this.products = {
            current_page: 1,
            data: [],
            total: 0,
            last_page: 1
          }
        }
      } catch (e) {
        console.error('Error fetching Etsy products:', e);
        this.products = {
          current_page: 1,
          data: [],
          total: 0,
          last_page: 1
        }
      } finally {
        this.is_loading = false
      }
    },

    handlePageChange(value) {
      this.fetchEtsyProducts()
    },

    async getEtsyApiDetail(listing_id) {
      try {
        this.etsy_data_dialog = true
        this.fetching_etsy_data = true
        let response = await axios.get(`admin/shops/${this.shop.id}/etsy-listing/${listing_id}/get-raw-data`)
        // if (response.data.success) {
        this.etsy_data = response.data
        // }
      } catch (e) {
      } finally {
        this.fetching_etsy_data = false
      }
    },

    async etsyProductDetail(etsyProductId)
    {
      router.push(`/etsy-products/${etsyProductId}`);
    },

    async shopifyProductDetail(shopifyId)
    {
      router.push(`/shopify-products/${shopifyId}`);
    },

    async setProductOnSync(item, force_update = false) {
      let shopify_id = item.shopify_product.id

      let response = await axios.post(`/set-product-upload-queue`, {
        shop_id: this.shop.id,
        product_id: shopify_id,
        is_forced: force_update,
      })

      this.$swal.fire({
        icon: 'success',
        title: 'Done',
        text: 'Product Sync Started',
      })
    },

    forceSyncProduct(item) {
      this.$swal
        .fire({
          title: 'Are you sure?',
          html: 'You want to force-upload the product? <br> <br> Doing this will ignore the price sync setting',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
        })
        .then(result => {
          if (result.isConfirmed) this.setProductOnSync(item, true)
        })
    },


    showSKUStatus(product)
    {
      let parent_data = product.upload_result;
      let prepared_data = product.data;

        let item = JSON.parse(parent_data);
        let prepared_item = JSON.parse(prepared_data ? prepared_data : "[]" );

        return `${item ? (item.has_variations ? "skus in variations" : item.skus[0]) : (prepared_item?.has_variations ? "sku in variations" : prepared_item?.skus)} <br/> ${item ? item.quantity : prepared_item?.quantity} in stock | ${item?.price.currency_code}
                ${item ? (item.price.amount / item.price.divisor) : prepared_item?.price} <br/> Status: ${product.etsy_state}`
    },
    
    async showImageDifference(item) {
      try {
        this.imageDifferenceDialog = true;
        this.loadingImages = true;
        this.imageData = {
          success: false,
          data: {
            etsy_images: [],
            shopify_images: [],
            etsy_image_count: 0,
            shopify_image_count: 0
          }
        };
        
        const response = await axios.get(`/admin/shops/${this.shop.id}/listing/${item.listing_id}/linked-product-images`);
        this.imageData = response.data;
      } catch (error) {
        console.error('Error fetching realtime image comparison:', error);
        this.imageData = {
          success: false,
          message: error.response?.data?.message || 'Failed to load realtime image comparison'
        };
      } finally {
        this.loadingImages = false;
      }
    },
  },
}
</script>
